import { supabase } from '@/lib/supabase'

export interface HairstyleConfig {
  id: string
  name: string
  description?: string
  hairstyle_description: string
  hair_color: string
  example_image_url?: string
  sort_order: number
  credit_cost: number
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface HairstyleGeneration {
  id: string
  user_id: string
  original_image_url: string
  face_details?: string
  hair_mask_url?: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  total_credit_cost: number
  google_account_used?: string
  error_message?: string
  created_at: string
  updated_at: string
  completed_at?: string
  // 新增字段：直接存储生成结果
  config_id?: string
  generated_image_url?: string
  prompt_used?: string
  processing_time_seconds?: number
  config?: HairstyleConfig
}

export interface HairstyleResult {
  id: string
  generation_id: string
  config_id: string
  result_image_url?: string
  prompt_used: string
  processing_time_seconds?: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error_message?: string
  created_at: string
  updated_at: string
  config?: HairstyleConfig
}

export interface GoogleAccount {
  id: string
  account_name: string
  email: string
  status: 'active' | 'inactive' | 'rate_limited' | 'error'
  usage_count: number
  error_count: number
}

class HairstyleService {
  /**
   * 获取所有活跃的发型配置
   */
  async getActiveHairstyleConfigs(): Promise<HairstyleConfig[]> {
    try {
      const { data, error } = await supabase
        .from('hairstyle_configs')
        .select('*')
        .eq('is_active', true)
        .order('sort_order')

      if (error) {
        console.error('Error fetching hairstyle configs:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error in getActiveHairstyleConfigs:', error)
      return []
    }
  }

  /**
   * 创建发型生成任务
   */
  async createHairstyleGeneration(
    userId: string,
    originalImageUrl: string,
    faceDetails?: string
  ): Promise<{ success: boolean; generationId?: string; error?: string }> {
    try {
      console.log('🚀 创建发型生成任务...')
      
      const { data, error } = await supabase.functions.invoke('create-hairstyle-generation', {
        body: {
          userId,
          originalImageUrl,
          faceDetails
        }
      })

      if (error) {
        console.error('Error creating hairstyle generation:', error)
        return { success: false, error: '创建生成任务失败' }
      }

      if (!data.success) {
        return { success: false, error: data.error || '创建生成任务失败' }
      }

      console.log('✅ 发型生成任务创建成功:', data.generationId)
      return { 
        success: true, 
        generationId: data.generationId
      }
    } catch (error) {
      console.error('Error in createHairstyleGeneration:', error)
      return { success: false, error: '创建生成任务失败' }
    }
  }

  /**
   * 获取用户的发型生成历史
   */
  async getUserHairstyleGenerations(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<HairstyleGeneration[]> {
    try {
      const { data, error } = await supabase
        .from('hairstyle_generations')
        .select(`
          *,
          config:hairstyle_configs(*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) {
        console.error('Error fetching hairstyle generations:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error in getUserHairstyleGenerations:', error)
      return []
    }
  }

  /**
   * 获取发型生成结果
   */
  async getHairstyleResults(generationId: string): Promise<HairstyleResult[]> {
    try {
      const { data, error } = await supabase
        .from('hairstyle_results')
        .select(`
          *,
          config:hairstyle_configs(*)
        `)
        .eq('generation_id', generationId)
        .order('created_at')

      if (error) {
        console.error('Error fetching hairstyle results:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error in getHairstyleResults:', error)
      return []
    }
  }

  /**
   * 获取发型生成状态
   */
  async getHairstyleGenerationStatus(generationId: string): Promise<{
    generation: HairstyleGeneration | null
    results: HairstyleResult[]
  }> {
    try {
      // 获取生成记录
      const { data: generation, error: genError } = await supabase
        .from('hairstyle_generations')
        .select('*')
        .eq('id', generationId)
        .single()

      if (genError) {
        console.error('Error fetching generation:', genError)
        return { generation: null, results: [] }
      }

      // 获取结果
      const results = await this.getHairstyleResults(generationId)

      return { generation, results }
    } catch (error) {
      console.error('Error in getHairstyleGenerationStatus:', error)
      return { generation: null, results: [] }
    }
  }

  /**
   * 启动发型生成处理
   */
  async startHairstyleProcessing(
    generationId: string,
    originalImageUrl: string,
    faceDetails?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🚀 启动发型生成处理...')
      
      const { data, error } = await supabase.functions.invoke('process-hairstyle-generation', {
        body: {
          generationId,
          originalImageUrl,
          faceDetails
        }
      })

      if (error) {
        console.error('Error starting hairstyle processing:', error)
        return { success: false, error: '启动处理失败' }
      }

      if (!data.success) {
        return { success: false, error: data.error || '启动处理失败' }
      }

      console.log('✅ 发型生成处理启动成功')
      return { success: true }
    } catch (error) {
      console.error('Error in startHairstyleProcessing:', error)
      return { success: false, error: '启动处理失败' }
    }
  }

  /**
   * 监控发型生成进度
   */
  async monitorHairstyleProgress(
    generationId: string,
    onProgress?: (status: { generation: HairstyleGeneration; results: HairstyleResult[] }) => void,
    onComplete?: (finalStatus: { generation: HairstyleGeneration; results: HairstyleResult[] }) => void
  ): Promise<void> {
    const pollInterval = 3000 // 3秒轮询一次
    const maxPollTime = 300000 // 最大轮询5分钟
    let pollCount = 0
    const maxPolls = maxPollTime / pollInterval

    const poll = async () => {
      try {
        const { generation, results } = await this.getHairstyleGenerationStatus(generationId)
        
        if (!generation) {
          console.error('无法获取生成状态')
          return
        }

        // 调用进度回调
        if (onProgress) {
          onProgress({ generation, results })
        }

        // 检查是否完成
        if (generation.status === 'completed' || generation.status === 'failed') {
          if (onComplete) {
            onComplete({ generation, results })
          }
          return
        }

        // 继续轮询
        pollCount++
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval)
        } else {
          console.warn('轮询超时，停止监控')
          if (onComplete) {
            onComplete({ generation, results })
          }
        }
      } catch (error) {
        console.error('轮询过程中发生错误:', error)
        if (onComplete) {
          onComplete({ generation: null as any, results: [] })
        }
      }
    }

    // 开始轮询
    poll()
  }

  /**
   * 删除发型生成记录
   */
  async deleteHairstyleGeneration(generationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('hairstyle_generations')
        .delete()
        .eq('id', generationId)

      if (error) {
        console.error('Error deleting hairstyle generation:', error)
        return { success: false, error: '删除失败' }
      }

      return { success: true }
    } catch (error) {
      console.error('Error in deleteHairstyleGeneration:', error)
      return { success: false, error: '网络请求失败' }
    }
  }

  /**
   * 计算发型生成所需积分
   */
  async calculateHairstyleGenerationCost(): Promise<number> {
    try {
      const configs = await this.getActiveHairstyleConfigs()
      return configs.reduce((total, config) => total + config.credit_cost, 0)
    } catch (error) {
      console.error('Error calculating cost:', error)
      return 0
    }
  }

  /**
   * 格式化发型生成状态显示
   */
  getStatusText(status: string): string {
    const statusMap: Record<string, string> = {
      'pending': '等待处理',
      'processing': '处理中',
      'completed': '已完成',
      'failed': '生成失败'
    }
    return statusMap[status] || status
  }

  /**
   * 获取状态颜色类
   */
  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'pending': 'text-yellow-600',
      'processing': 'text-blue-600',
      'completed': 'text-green-600',
      'failed': 'text-red-600'
    }
    return colorMap[status] || 'text-gray-600'
  }

  /**
   * 格式化处理时间显示
   */
  formatProcessingTime(seconds?: number): string {
    if (!seconds) return '未知'
    
    if (seconds < 60) {
      return `${seconds}秒`
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}分${remainingSeconds}秒`
    } else {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return `${hours}小时${minutes}分钟`
    }
  }

  /**
   * 根据发型配置和用户自定义提示词生成完整的AI提示词
   */
  generatePrompt(config: HairstyleConfig, customPrompt?: string): string {
    let prompt = `Change the person's hairstyle to ${config.hairstyle_description} with ${config.hair_color} color`
    
    if (customPrompt && customPrompt.trim()) {
      prompt += ` and ${customPrompt.trim()}`
    }
    
    return prompt
  }

  /**
   * 获取用于前端显示的发型配置（包含示例图片）
   */
  async getHairstyleConfigsForDisplay(): Promise<(HairstyleConfig & { imageUrl: string })[]> {
    try {
      const configs = await this.getActiveHairstyleConfigs()
      
      // 为每个配置添加示例图片URL，优先使用数据库中的example_image_url
      return configs.map((config, index) => ({
        ...config,
        imageUrl: config.example_image_url || `/images/hairstyles/hairstyle-${index + 1}.png`
      }))
    } catch (error) {
      console.error('Error getting hairstyle configs for display:', error)
      return []
    }
  }

  /**
   * 更新发型生成记录
   */
  async updateHairstyleGeneration(
    generationId: string,
    configId: string,
    generatedImageUrl: string,
    status: 'success' | 'failed',
    errorMessage?: string,
    promptUsed?: string,
    processingTimeSeconds?: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`📝 更新发型生成记录: ${generationId}`)
      console.log(`🎨 配置ID: ${configId}`)
      console.log(`📊 状态: ${status}`)

      // 直接更新hairstyle_generations表
      const updateData: any = {
        config_id: configId,
        status: status,
        updated_at: new Date().toISOString()
      }

      if (status === 'success') {
        updateData.generated_image_url = generatedImageUrl
        updateData.completed_at = new Date().toISOString()
        if (promptUsed) {
          updateData.prompt_used = promptUsed
        }
        if (processingTimeSeconds) {
          updateData.processing_time_seconds = processingTimeSeconds
        }
      } else if (errorMessage) {
        updateData.error_message = errorMessage
      }

      const { error } = await supabase
        .from('hairstyle_generations')
        .update(updateData)
        .eq('id', generationId)

      if (error) {
        console.error('❌ 更新发型生成记录失败:', error)
        return { success: false, error: error.message }
      }

      console.log('✅ 发型生成记录更新成功')
      return { success: true }

    } catch (error) {
      console.error('❌ 更新发型生成记录时发生错误:', error)
      return { success: false, error: error instanceof Error ? error.message : '未知错误' }
    }
  }
}

// 导出单例实例
export const hairstyleService = new HairstyleService()
export default hairstyleService
